import { mergeStyleSets, Toggle } from "@fluentui/react";
import { AddIcon, Button, Card, Checkbox, ChevronDownIcon, ChevronEndIcon, Dialog, Dropdown, Flex, Input, ProviderConsumer, SearchIcon, Text, TextArea } from '@fluentui/react-northstar';
import { themeNames } from '@fluentui/react-teams';
import { ThemePrepared } from '@fluentui/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom";
import UpgradeSVG from '../../../src/svg/upgrade.svg';
import DeleteIcon from '../../svg/delete.svg';
import { appState } from '../AppState';
import { CustomTimePicker } from "../custom-properties/CustomTimePicker";
import { CustomPropertyDataType_LABELS } from '../custom-properties/utils/constants';
import { Form } from '../forms/FormsTable';
import MgtCss from '../mgt.module.css';
import { getCachedUsers, searchUsers } from '../shared/cache/PlatformUserCache';
import { ThemeColorScheme } from '../shared/common/TeamsTheme';
import { DatePicker } from '../shared/components/DatePicker/DatePicker';
import { LabelElement } from '../shared/components/Modal/Modal';
import { SwitchButton } from '../shared/components/SwitchButton/SwitchButton';
import { platformService } from '../shared/services/platform.service';
import { AutomationOperators, AutomationToggleStyle, BUTTONS, CustomProperty_TimeInputWrapperClass, FILTERS_STRINGS, LIFECYCLE } from '../shared/utils/constants';
import { parseIfJSON, toTitleCase } from '../shared/utils/helper';
import { toolbarButtonColor } from '../tikit/ticketHelper';
import { AdaptiveCardElement } from './AdaptiveCardElement';
import { FORM_ACTIONS, FORM_FILTER_OPERATOR } from './CustomForms/constants';
import { nullOperators } from './CustomForms/CustomForm.Interface';
import { findCardInputItems } from './CustomForms/CustomForm.Service';
import { TikitPeoplePicker } from "../shared/components/PeoplePicker/TikitPeoplePicker";

export interface Filter {
  Id: number;
  Property: string,
  Operator: string,
  Value: string,
  Operators: DropdownDataModel[]
  Type: string,
  InputChoices: DropdownDataModel[],
  DataSouceId: number
  PropertyDisplayText: string,
  DisableValueField: boolean
}
export interface RuleGroup {
  Id: number;
  LogicalOperator: string,
  Filters: Filter[]
}

export interface TemplateForm {
  Id: number;
  Form: Form,
  IsExpand: boolean,
  UseDefaultLogic: boolean,
  UseLogicBuilder: boolean,
  Rules: RuleGroup[],
  ActionType: string
}


export const TemplateForm = ({ setFormList, formList, templateData, dispatchData, isFeatureEnabled }
) => {
  const { t } = useTranslation();
  const api = new platformService();
  const currentState = appState();
  const [openFormSelectionDialog, setFormSelectionDialog] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [selectedCardIndex, setSelectedCardIndex] = useState(null);
  const [forms, setForms] = useState([]);
  const [templateForms, setTemplateForms] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDeleteTransitionDialog, setOpenDeleteTransitionDialog] = useState(false);
  const [formDropDownList, setFormDropDownList] = useState([]);
  const [currentFormIndex, setCurrentFormIndex] = useState(0);
  const [selectedFormIndexToDelete, setSelectedFormIndexToDelete] = useState(null);
  const [dataSources, setDataSources] = useState([]);
  const [selectedTransitionIndexToDelete, setSelectedTransitionIndexToDelete] = useState(null);
  const [filteredForms, setFilteredForms] = useState(forms);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [filterProperties, setFilterProperties] = useState([]);

  const classNames = mergeStyleSets({
    customForms: {
      '.ac-textBlock:not(.ac-textBlock ~ .ac-textBlock)': {
        maxWidth: '100%'
      }
    }
  })

  /**
    * action events
    */
  const onAddNewFormClick = () => {
    fetchAllForms();
    setCurrentFormIndex(templateForms.length)
    setFormSelectionDialog(true);
  }

  const onSubmitForm = () => {
    if (!selectedForm) {
      setFormSelectionDialog(false);
      return;
    }

    const alreadyExists = templateForms.some((templateForm) => templateForm.Form.Id === selectedForm.Form.Id);
    if (alreadyExists) {
      setDuplicateDialogOpen(true);
      return;
    }

    setTemplateForms([...templateForms, selectedForm]);
    setFormSelectionDialog(false);

    setFormList(selectedForm);

    //reset card selection values
    setSelectedCardIndex(null);
    setSelectedForm(null);
  }

  const onExpandForm = (index: number, isExpand: boolean) => {
    templateForms[index].IsExpand = isExpand;
    setTemplateForms([...templateForms]);
  }

  const onSelectCard = (checked: boolean, index: number, form: any) => {
    if (!checked) {
      setSelectedForm(null);
      setSelectedCardIndex(null);
    } else {
      setSelectedCardIndex(index);
      const newFilter = { Property: "", Operator: "Equal", Value: "", Operators: FORM_FILTER_OPERATOR.default };
      const newRuleGroup = {
        LogicalOperator: "And",
        Filters: [newFilter]
      }
      const newTranstion = {
        Name: t('template.template-form-new-form-transition'),
        UseDefaultLogic: true,
        UseLogicBuilder: false,
        ActionType: "Submit",
        TransitionGroups: [newRuleGroup]
      }
      const newTemplateForm = {
        Name: form.Name,
        Form: { Id: form.Id, Name: form.Name, Definition: form.Definition },
        IsExpand: false,
        Order: currentFormIndex,
        SubmitButtonText: t('template.template-form-submit'),
        Transitions: [newTranstion]
      }
      setSelectedForm(newTemplateForm);
    }
  }

  const onAddNewRuleGroupClick = (templateFormIndex: number, transitionIndex: number) => {
    const setFormList = templateForms.map((templateForm, i) => {
      if (i === templateFormIndex) {
        const newFilter = { Property: "", Operator: "Equal", Value: "", Operators: FORM_FILTER_OPERATOR.default };
        const newRuleGroup = {
          LogicalOperator: "And",
          Filters: [newFilter]
        }
        templateForm.Transitions[transitionIndex].TransitionGroups.push(newRuleGroup);
      }
      return templateForm;
    });
    setTemplateForms(setFormList);
  }

  const onRemoveFilterClick = (templateFormIndex: number, transitionIndex: number, ruleGroupIndex: number, filterIndex: number) => {

    const setFormList = templateForms.map((templateForm, i) => {
      if (i === templateFormIndex) {
        templateForm.Transitions[transitionIndex].TransitionGroups[ruleGroupIndex].Filters.splice(filterIndex, 1);
      }
      return templateForm;
    });
    setTemplateForms(setFormList);
  }

  const onRemoveRuleGroupClick = (templateFormIndex: number, transitionIndex: number, ruleGroupIndex: number) => {
    const setFormList = templateForms.map((templateForm, i) => {
      if (i === templateFormIndex) {
        templateForm.Transitions[transitionIndex].TransitionGroups.splice(ruleGroupIndex, 1);
      }
      return templateForm;
    });
    setTemplateForms(setFormList);
  }

  const onAddNewFilterClick = (templateFormIndex: number, transitionIndex: number, ruleGroupIndex: number) => {
    const setFormList = templateForms.map((templateForm, i) => {
      if (i === templateFormIndex) {
        const newFilter = { Property: "", PropertyDisplayText: "", Operator: "Equal", Value: "", Operators: FORM_FILTER_OPERATOR.default };
        templateForm.Transitions[transitionIndex].TransitionGroups[ruleGroupIndex].Filters.push(newFilter);
      }
      return templateForm;
    });
    setTemplateForms(setFormList);
  }

  const onFilterPropertySelect = (templateFormIndex: number, transitionIndex: number, ruleGroupIndex: number, filterIndex: number, selectedProperty: any, globalTheme: ThemePrepared) => {
    const setFormList = templateForms.map((templateForm, i) => {
      if (i === templateFormIndex) {
        const operators = getFilterOperators(selectedProperty.type);

        templateForm.Transitions[transitionIndex].TransitionGroups[ruleGroupIndex].Filters[filterIndex].Property = selectedProperty.key;
        templateForm.Transitions[transitionIndex].TransitionGroups[ruleGroupIndex].Filters[filterIndex].PropertyDisplayText = selectedProperty.label;
        templateForm.Transitions[transitionIndex].TransitionGroups[ruleGroupIndex].Filters[filterIndex].Operators = operators;
        templateForm.Transitions[transitionIndex].TransitionGroups[ruleGroupIndex].Filters[filterIndex].Operator = operators[0].value;
        templateForm.Transitions[transitionIndex].TransitionGroups[ruleGroupIndex].Filters[filterIndex].Value = "";
        templateForm.Transitions[transitionIndex].TransitionGroups[ruleGroupIndex].Filters[filterIndex].Type = selectedProperty.type;
        templateForm.Transitions[transitionIndex].TransitionGroups[ruleGroupIndex].Filters[filterIndex].InputChoices = selectedProperty.choices;
        templateForm.Transitions[transitionIndex].TransitionGroups[ruleGroupIndex].Filters[filterIndex].DataSouceId = selectedProperty.dataSource;

        return templateForm;
      } else {
        return templateForm;
      }
    });
    setTemplateForms(setFormList);
  }

  const onFilterOperatorSelect = (templateFormIndex: number, transitionIndex: number, ruleGroupIndex: number, filterIndex: number, selectedOperator: any, globalTheme: ThemePrepared) => {
    const setFormList = templateForms.map((templateForm, i) => {
      if (i === templateFormIndex) {
        templateForm.Transitions[transitionIndex].TransitionGroups[ruleGroupIndex].Filters[filterIndex].Operator = selectedOperator.value;
        if ([AutomationOperators.IsNull, AutomationOperators.IsNotNull].includes(selectedOperator.value))
          templateForm.Transitions[transitionIndex].TransitionGroups[ruleGroupIndex].Filters[filterIndex].Value = "";
        
        return templateForm;
      } else {
        return templateForm;
      }
    });
    setTemplateForms(setFormList);
  }

  const onFilterValueChange = (templateFormIndex: number, transitionIndex: number, ruleGroupIndex: number, filterIndex: number, filterValue: any, globalTheme: ThemePrepared) => {
    const setFormList = templateForms.map((templateForm, i) => {
      if (i === templateFormIndex) {
        templateForm.Transitions[transitionIndex].TransitionGroups[ruleGroupIndex].Filters[filterIndex].Value = filterValue;
      }
      return templateForm;
    });
    setTemplateForms(setFormList);
  }

  /**
    * methods
    */
  const fetchAllForms = useCallback(async () => {
    const results = await Promise.all([
      api.getForms("?filter=IsArchived eq false")
    ]);
    const allForms = results[0].data?.value ?? [];
    allForms.filter(x => x.Definition && x.Definition !== "").map((item: Form) => {
      item.Definition = JSON.parse(item.Definition);
      item.Definition["hidden"] = false;

      return item;
    }
    );
    setForms(allForms);
    setFilteredForms(allForms);
  }, [])

  const getFilterProperties = (templateForm: any) => {
    if (!filterProperties.some(x=>x.guid==templateForm.Guid))
    {
      const properties: DropdownDataModel[] = [];
      const formDefintion = (typeof (templateForm.Form.Definition) == 'string') ? JSON.parse(templateForm.Form.Definition) : templateForm.Form.Definition;
      const inputElements = findCardInputItems(formDefintion.body, currentState.customProperties);
      const excludedTicketProperties = ["Ticket.Assignment", "Ticket.StatusOfMyRequests", "Ticket.SubmitRequest"];

      inputElements.forEach(prop => {
        const isTicketProperty = prop.id.toLowerCase().includes("ticket.");
        const isCustomProperty = prop.isCustomProperty && prop.dataType;
        let propertyType = isTicketProperty ? prop.id : prop.type;
        if (isCustomProperty) {
          if (prop.isMultiSelectField && CustomPropertyDataType_LABELS[prop.dataType] == CustomPropertyDataType_LABELS.LIST)
            propertyType = CustomPropertyDataType_LABELS[prop.dataType] + "_Multi";
          else
            propertyType = CustomPropertyDataType_LABELS[prop.dataType];
        }
        let propertyLabel = isTicketProperty || isCustomProperty
          ? prop.displayLabel ?? prop.displayTitle
          : prop.label ?? prop.title;

          //replace Ticket.Assignment with Ticket.Assignee
          if (propertyType == "Ticket.Assignment") {
            propertyType = "Ticket.Assignee";
            propertyLabel = "Assignee"
          }

        if (!excludedTicketProperties.includes(propertyType)) {
          properties.push({
            key: prop.id,
            value: prop.id,
            label: propertyLabel ?? prop.id,
            header: propertyLabel ?? prop.id,
            type: propertyType,
            choices: prop.choices,
            dataSource: prop.dataSource
          });
        }
      });

      setFilterProperties([...filterProperties,{guid:templateForm.Guid, properties:properties}]);
      return properties;
    }
    else
    {
      return filterProperties.find(x=>x.guid==templateForm.Guid).properties;
    }
  }

  const getFilterOperators = (inputType: any) => {
    switch (inputType) {
      case 'Ticket.Request':
      case 'Input.Text':
        return FORM_FILTER_OPERATOR.text;
      case 'Input.Number':
      case CustomPropertyDataType_LABELS.INT:
        return FORM_FILTER_OPERATOR.number;
      case 'Ticket.DueDate':
      case 'Ticket.ResolutionDate':
      case 'Input.Date':
      case CustomPropertyDataType_LABELS.DATE:
        return FORM_FILTER_OPERATOR.date;
      case 'Input.Time':
      case CustomPropertyDataType_LABELS.TIME:
        return FORM_FILTER_OPERATOR.time;
      case 'Input.ChoiceSet':
      case 'Ticket.Status':
      case 'Ticket.TicketType':
      case 'Ticket.Category':
      case 'Ticket.Priority':
      case 'Ticket.Team':
      case 'Ticket.Group':
      case 'Ticket.Assignee':
      case CustomPropertyDataType_LABELS.LIST:
        return FORM_FILTER_OPERATOR.choiceset;
      case 'Input.Toggle':
      case CustomPropertyDataType_LABELS.BOOL:
        return FORM_FILTER_OPERATOR.toggle;
      case 'Ticket.AffectedUsers':
        return FORM_FILTER_OPERATOR.affecteduser;
      case 'Ticket.Requester':
        return FORM_FILTER_OPERATOR.user;
      case 'Ticket.AddComment':
        return FORM_FILTER_OPERATOR.comment;
      case 'Ticket.UploadFile':
        return FORM_FILTER_OPERATOR.attachment;
      case 'Ticket.Collaborators':
        return FORM_FILTER_OPERATOR.collaborator;
      case 'Ticket.Tags':
      case CustomPropertyDataType_LABELS.STRING:
      case CustomPropertyDataType_LABELS.LIST + "_Multi":
        return FORM_FILTER_OPERATOR.tag;
      default:
        return FORM_FILTER_OPERATOR.default;
    }
  }

  const getFilterDropdownValue = (formIndex: number, filter: any) => {

    let filterDropdownValues = [] as DropdownDataModel[];

    switch (filter.Type) {
      case 'Ticket.Status':
        currentState.ticketStatus.filter(x => !x.IsArchived || !x.IsDeleted).forEach((prop) => {
          filterDropdownValues.push({ key: prop.Id.toString(), value: prop.Value, label: prop.Value, header: prop.Value })
        });
        break;
      case 'Ticket.TicketType':
        currentState.ticketTypes.filter(x => !x.IsArchived || !x.IsDeleted).forEach((prop) => {
          filterDropdownValues.push({ key: prop.Id.toString(), value: prop.Value, label: prop.Value, header: prop.Value })
        });
        break;
      case 'Ticket.Category':
        currentState.categories.filter(x => !x.IsArchived || !x.IsDeleted).forEach((prop) => {
          filterDropdownValues.push({ key: prop.Id.toString(), value: prop.Value, label: prop.Value, header: prop.Value })
        });
        break;
      case 'Ticket.Priority':
        currentState.priority.filter(x => !x.IsArchived || !x.IsDeleted).forEach((prop) => {
          filterDropdownValues.push({ key: prop.Id.toString(), value: prop.Value, label: prop.Value, header: prop.Value })
        });
        break;
      case 'Ticket.Team':
        currentState.teams.forEach((prop) => {
          filterDropdownValues.push({ key: prop.Id.toString(), value: prop.FriendlyName, label: prop.FriendlyName, header: prop.FriendlyName })
        });
        break;
      case 'Ticket.Group':
        currentState.supportgroups.forEach((prop) => {
          filterDropdownValues.push({ key: prop.Id.toString(), value: prop.Name, label: prop.Name, header: prop.Name })
        });
        break;
      case 'Ticket.Tags':
        currentState.tags.forEach((prop) => {
          filterDropdownValues.push({ key: prop.Id.toString(), value: prop.Name, label: prop.Name, header: prop.Name })
        });
        break;
      case CustomPropertyDataType_LABELS.LIST:
      case CustomPropertyDataType_LABELS.LIST + "_Multi":
        const customProp = currentState.customProperties.find(cp => cp.PropertyId == filter.Property);
        if (customProp)
          customProp.CustomPropertyListItems.forEach((prop) => {
            if (!prop.IsArchived && !prop.IsDeleted)
              filterDropdownValues.push({ key: prop.Id.toString(), value: prop.Value, label: prop.Value, header: prop.Value })
          });
        break;
      case 'Ticket.Collaborators':
      case 'Ticket.Assignee':
        const assigneeList = currentState.usersInRole.sort((a, b) => a.FullName.localeCompare(b.FullName));
        assigneeList.forEach((user) => {
          filterDropdownValues.push({ key: user.Id.toString(), value: user.FullName, label: user.FullName, header: user.FullName })
        });
        break;
      case 'Input.ChoiceSet':
      default:
        const formDefintion = parseIfJSON(templateForms[formIndex].Form.Definition);
        const allInputs = findCardInputItems(formDefintion.body, currentState.customProperties);
        const choiceSet = allInputs.filter(input => (input.type === "Input.ChoiceSet" && input.id === filter.Property))[0];

        if (choiceSet) {
          const choices = choiceSet.choices;
          choices.forEach(item => {
            filterDropdownValues.push({ key: item.value, value: item.title, label: item.title, header: item.title })
          });
        }
        break;
    }

    //apply datasource filter when needed
    if (filter.DataSouceId != null) {
      const dSource = dataSources.filter(x => x.Id == filter.DataSouceId)[0];
      const dataSourceIds = dSource ? dSource.SelectedItems.split(",") : [];

      if (dataSourceIds.length > 0)
        return filterDropdownValues.filter(item => dataSourceIds.includes(item.key));
    }

    return filterDropdownValues;
  }


  const getMultipleFilterValue = (inputType: any, filter: any) => {
    const dropdownValueList: any[] = getFilterDropdownValue(inputType, filter);
    const valuesArr = filter.Value.trim().split(",").map(s => s.trim()).filter(Boolean);
    return dropdownValueList.filter(x => valuesArr.includes(x.key.toString()));
  }

  const getSelectedUsers = async (theseUsers: PlatformUser[], propertyValue: string) => {
    const selectedUsers = theseUsers;
    let selectedUser = [];
    if (['Ticket.Requester', 'Ticket.AffectedUsers'].includes(propertyValue)) {
      for (let user of selectedUsers)
        selectedUser.push(await searchUsers({ Email: user.Email ?? user.UserName, GivenName: user.GivenName, FamilyName: user.FamilyName, UserName: user.UserName, AadObjectId: user.AadObjectId } as PlatformUser));

      return selectedUser.filter(x => x).map(x => x.Id).join(',');
    } else {
      selectedUsers.forEach(user => {
        const userToMap = (user?.UserName) ? user.UserName : user.FullName
        selectedUser.push({ displayName: user.FullName, userPrincipalName: userToMap, id: user.Id });
      });
      return (JSON.stringify(selectedUser))
    }
  };

  const isValueFieldDisabled = (filter: any) => {
    return nullOperators.includes(filter.Operator.toLowerCase()) || filter.Property == "";
  }

  useEffect(() => {
    setTemplateForms(formList ?? []);

    const load = async () => {
      const result = await api.getDataSources(`${FILTERS_STRINGS.FILTERS_URL_PARAMS}`)
      setDataSources(result.data.value);
    };
    load();


  }, []);

  useEffect(() => {
    setFormDropDownList(templateForms == undefined ? [] : templateForms.map((form, i) => { return { key: form.Order, value: form.Name, label: form.Name, header: form.Name } }));
    templateData.NoCustomForm = templateForms.length <= 0;
    templateData.TemplateForms = templateForms;
    dispatchData(templateData);
  }, [templateForms])

  const setDatasourceOnFilters = (groups: any[], formInputs: any[]) => {
    groups.forEach(g => {
      g.Filters.forEach(f => {
        const formInput = formInputs.find(i => i.key == f.Property);
        if (formInput && !Number.isNaN(parseInt(formInput.dataSource)))
          f.DataSouceId = parseInt(formInput.dataSource);
      });
    })
  }

  const handleFormSearch = (event: any) => {
    resetFormSelection();

    const term = event.target.value;
    const newResults = forms.filter(form =>
      form.Name.toLowerCase().includes(term.toLowerCase())
    );

    setFilteredForms(newResults);

    if (term == "") {
      resetFormSelection();
    }
  };

  const resetFormSelection = () => {
    setSelectedForm(null);
    setSelectedCardIndex(null);
  }



  /**
    * renders
    */

  const renderSummaryCardSection = (globalTheme: ThemePrepared) => {
    return <>
      <Flex column gap="gap.medium">
        <Flex.Item>
          <Flex column>
            <Text content={t('template.template-form-summary-card')} weight="bold" size="large" />
            <Text content={t('template.template-form-summary-card-message')} />
          </Flex>
        </Flex.Item>
        <Flex.Item>
          <Checkbox
            defaultChecked
            checked={templateData.SummaryCardEnabled}
            label={
              <span>
                <b>{t('template.template-form-show-summary-card')}</b> <br /> {t('template.template-form-show-summary-card-message')}
              </span>
            }
            onChange={(e, { checked }) => {
              templateData.SummaryCardEnabled = checked;
              dispatchData(templateData);
            }}
          />
        </Flex.Item>
        <Flex.Item>
          <Checkbox
            disabled={!templateData.SummaryCardEnabled}
            defaultChecked
            checked={templateData.SummaryCardEnabled ? templateData.ShowFormNameOnSummary : false}
            label={
              <span>
                <b>{t('template.template-form-show-form-name')}</b> <br /> {t('template.template-form-show-form-name-message')}
              </span>
            }
            onChange={(e, { checked }) => {
              templateData.ShowFormNameOnSummary = checked;
              dispatchData(templateData);
            }} />
        </Flex.Item>
        <Flex.Item>
          <Flex column gap="gap.medium" vAlign="center" space="between">
            <Text content={t('template.template-form-summary-card-header-text')} weight="bold" className="mr-2" />
            <TextArea disabled={!templateData.SummaryCardEnabled} className="py-2 mb-10" variables={{ height: "50px" }} value={templateData.SummaryHeaderText}
              onChange={(e, p) => {
                templateData.SummaryHeaderText = p.value
                dispatchData(templateData);
              }} />
          </Flex>
        </Flex.Item>
      </Flex>
    </>
  }

  const renderFormList = (globalTheme: ThemePrepared) => {
    return <>
      <Flex column gap="gap.large">
        <Flex.Item>
          <Flex column gap="gap.smaller">
            {renderTemplateFormItems(globalTheme)}
          </Flex>
        </Flex.Item>
      </Flex>
    </>
  }

  const renderHeader = () => {
    return <>
      <Flex styles={{ paddingTop: '1.75rem' }}>
        <Text content={t('template.template-form-header')} weight="bold" size="large" />
      </Flex>
      <Flex styles={{ padding: '0.5rem 0' }}>
        <Text content={t('template.template-form-subheader')} />
      </Flex>
    </>
  }

  const renderAddFormButton = (globalTheme: ThemePrepared) => {
    return <>
      <Flex>
        <Button
          icon={<AddIcon />}
          text
          primary
          content={t('template.template-form-add-form')}
          onClick={onAddNewFormClick}
          disabled={!isFeatureEnabled}
          style={{ color: toolbarButtonColor(isFeatureEnabled, globalTheme.siteVariables.colorScheme), height: '52px' }}
        />
        {!isFeatureEnabled ? <UpgradeSVG height={25} width={25} className='inline-block' /> : <></>}
      </Flex>
    </>
  }

  const renderAddRuleGroupButton = (globalTheme: ThemePrepared, templateFormIndex: number, transitionIndex: number) => {
    return <>
      <Button
        icon={<AddIcon styles={{ color: globalTheme.siteVariables.colorScheme.brand.foreground }} />}
        text
        primary
        content={<span style={{ color: globalTheme.siteVariables.colorScheme.brand.foreground }}>{t('template.template-form-add-rule-group')}</span>}
        onClick={() => onAddNewRuleGroupClick(templateFormIndex, transitionIndex)}
        style={{marginLeft:'-0.5rem'}}
      />
    </>
  }

  const renderAddTransitionButton = (globalTheme: ThemePrepared, templateFormIndex: number) => {
    const onAddTransitionClick = (templateFormIndex: number) => {
      const setFormList = templateForms.map((templateForm, i) => {
        if (i === templateFormIndex) {
          const newFilter = { Property: "", Operator: "Equal", Value: "", Operators: FORM_FILTER_OPERATOR.default };
          const newRuleGroup = {
            Form: templateForms[templateFormIndex],
            LogicalOperator: "And",
            Filters: [newFilter]
          }
          const newTranstion = {
            Name: t('template.template-form-new-form-transition'),
            UseDefaultLogic: true,
            UseLogicBuilder: false,
            ActionType: "Submit",
            TransitionGroups: [newRuleGroup]
          }

          templateForm.Transitions.push(newTranstion);
        }
        return templateForm;
      });
      setTemplateForms(setFormList);
    }

    return <>
      <Button
        icon={<AddIcon styles={{ color: globalTheme.siteVariables.colorScheme.brand.foreground }} />}
        text
        primary
        content={<span style={{ color: globalTheme.siteVariables.colorScheme.brand.foreground }}>{t('template.template-form-add-transition')}</span>}
        onClick={() => onAddTransitionClick(templateFormIndex)}
      />
    </>
  }

  const renderAddFormDialog = (globalTheme: any) => {
    return <>
      <Flex column style={{ flex: 1, maxHeight: '70vh', overflow: 'auto' }} gap="gap.large">
        <Flex.Item>
          <Flex gap="gap.medium">
            <Text content={t('template.template-form-predefined-forms-message')} />
          </Flex>
        </Flex.Item>
        <Flex.Item>
          <Flex >
            <Text content={t('template.template-form-avaialble-forms')} weight="bold" />
            <Flex.Item push>
              <Input icon={<SearchIcon />} placeholder={t('template.search')} onChange={handleFormSearch} />
            </Flex.Item>
          </Flex>
        </Flex.Item>
        <Flex.Item>
          <Flex gap="gap.medium" className={MgtCss.cardSelectionContainer}>
            {renderItems(globalTheme)}
          </Flex>
        </Flex.Item>
      </Flex>
    </>
  }

  const renderTemplateFormDetail = (globalTheme: ThemePrepared, templateForm: any) => {
    const transitionText = templateForm.Transitions.length == 1 ? t('template.template-form-count-label') : t('template.template-form-transitions');
    const transitionCount = templateForm.Transitions.length > 0 ? templateForm.Transitions.length + ' ' + transitionText : "";
    return (
      <div style={{ borderTop: `1px solid ${globalTheme.siteVariables.colorScheme.brand.foreground1}`, ...ThemeColorScheme(globalTheme.siteVariables) }}>
        <div className={`p-2`} style={{
          margin: '0 auto', width: '95%', backgroundColor: globalTheme.siteVariables.theme === themeNames.Dark ?
            globalTheme.siteVariables.colorScheme.default.background1 : globalTheme.siteVariables.colorScheme.default.background3
        }}>
          <Flex className={`p-2`} space='between' vAlign='center'>
            <Flex.Item>
              <span style={{ fontWeight: 700, fontSize: '12px', lineHeight: '16px' }}>{t('template.template-form-transitions')}</span>
            </Flex.Item>
            <Flex.Item>
              <Flex>
                <Flex.Item>
                  <div style={{ fontSize: '10px', fontWeight: 400, lineHeight: '13.3px', width: '100%' }}>{transitionCount}</div>
                </Flex.Item>
              </Flex>
            </Flex.Item>
          </Flex>
        </div>
      </div>
    )
  }

  const renderItems = (globalTheme: any) => filteredForms.filter(x => x.Definition !== "").map((form, index) => (
    <>
      <Card compact ghost styles={{ width: '46%', marginTop: '1rem', "&:hover": { background: 'transparent' }, height: 'fit-content' }}
        onClick={() => {
          onSelectCard(selectedCardIndex != index, index, form);
        }}>
        <Card.TopControls style={{ zIndex: 1 }}>
        </Card.TopControls>
        <Card.Body fitted styles={{ paddingTop: '0.85rem' }}>
          <Flex column gap="gap.small">
            <Text content={form.Name} className="ml-2" weight="semibold" styles={{ maxWidth: '300px', whiteSpace: 'normal', wordBreak: 'break-word', marginLeft: '0.5rem' }} />
            <AdaptiveCardElement jsonString={JSON.stringify(form.Definition)} cardProps={{ styles: { height: '350px', overflow: 'hidden', pointerEvents: 'none' }, selected: selectedCardIndex == index }} containerClassName={classNames.customForms} clear={true} globalTheme={globalTheme}/>
          </Flex>
        </Card.Body>
      </Card>
    </>
  ));

  const renderTemplateFormItems = (globalTheme: ThemePrepared) => {
    const getFormURL = (formId: number) => { return "/settings/forms?id=" + formId; }
    return templateForms.map((templateForm, index) => (
      <>
        <Flex vAlign="center" space="between" style={{ borderBottom: templateForm.IsExpand ? `1px solid rgb(98, 100, 167)` : '' }}>
          <Flex.Item >
            {templateForm.IsExpand ?
              <Flex gap="gap.small" vAlign="center">
                <Flex.Item>
                  <span onClick={isFeatureEnabled ? () => onExpandForm(index, !templateForm.IsExpand) : undefined} className={`cursor-pointer`} title={`Collapse`}><ChevronDownIcon /></span>
                </Flex.Item>
                <Flex.Item >
                  <span style={{ cursor: "pointer", color: globalTheme.siteVariables.colorScheme.brand.foregroundActive }}>
                    <Link to={getFormURL(templateForm.Form?.Id)} target="_blank">{templateForm.Form?.Name}</Link>
                  </span>
                </Flex.Item>
              </Flex> :
              <Flex gap="gap.small" vAlign="center">
                <Flex.Item>
                  <span onClick={isFeatureEnabled ? () => onExpandForm(index, !templateForm.IsExpand) : undefined} className={`cursor-pointer`} title={`Collapse`}><ChevronEndIcon /></span>
                </Flex.Item>
                <Flex.Item >
                  <span style={{ cursor: "pointer", color: globalTheme.siteVariables.colorScheme.brand.foregroundActive }}>
                    <Link to={getFormURL(templateForm.Form.Id)} target="_blank">{templateForm.Form.Name}</Link>
                  </span>
                </Flex.Item>
              </Flex>
            }
          </Flex.Item>
          {isFeatureEnabled && <Flex.Item>
            <Flex gap={`gap.small`} vAlign={`center`}>
              <Flex.Item>
                <div>
                  <Button
                    icon={<DeleteIcon height={14} width={14} stroke={`${globalTheme.siteVariables.colorScheme.brand.foreground}`} />}
                    text
                    iconOnly
                    size="small"
                    title={t('template.template-form-remove')}
                    content={
                      <span style={{ color: globalTheme.siteVariables.colorScheme.brand.foreground, fontSize: '12px' }}>
                        {t('automation.automation-filter.remove')}
                      </span>
                    }
                    onClick={async e => {
                      e.stopPropagation();
                      setSelectedFormIndexToDelete(index);
                      setOpenDeleteDialog(true);
                    }}
                  />
                </div>
              </Flex.Item>
            </Flex>
          </Flex.Item>}
        </Flex>
        {templateForm.IsExpand &&
          <Flex column className='ml-10 mt+15' gap="gap.small" >
            <Flex><Text content={t('template.template-form-transitions')} weight="bold"></Text></Flex>
            <Flex column gap="gap.medium">{renderTemplateFormTransitions(globalTheme, templateForm.Transitions, index)}</Flex>
            <Flex>{renderAddTransitionButton(globalTheme, index)}</Flex>
            <Flex><Text content={t('template.template-form-managing-submit-button')} weight="bold"></Text></Flex>
            <Flex column gap="gap.medium" vAlign="center" space="between">
              <em>{t('template.template-form-submit-button-text')}</em>
              <Input styles={{ width: '60%' }} fluid value={templateForm.SubmitButtonText}
                onChange={(e, p) => {
                  templateForms[index].SubmitButtonText = p.value;
                  setTemplateForms([...templateForms]);
                  setFormList();
                }}
              ></Input>
            </Flex>
          </Flex>}
        {!templateForm.IsExpand && renderTemplateFormDetail(globalTheme, templateForm)}

        <Dialog
          open={openDeleteDialog}
          onCancel={() => setOpenDeleteDialog(false)}
          cancelButton={toTitleCase(BUTTONS.CANCEL)}
          confirmButton={toTitleCase(BUTTONS.DELETE)}
          onConfirm={(e) => {
            const filteredForms = templateForms
              .filter((_, index) => index !== selectedFormIndexToDelete)
              .sort((a, b) => a.Order - b.Order)
              .map((item, index) => {
              if (item.Transitions) {
                item.Transitions.forEach(transition => {
                  // Reset the TransitionFormIndex when the referenced form is deleted
                  if (transition.TransitionFormIndex === selectedFormIndexToDelete) {
                    transition.TransitionFormIndex = -1;
                  }
                });
              }
              return { ...item, Order: index };
              });
            setTemplateForms(filteredForms);
            setFormList();
            setOpenDeleteDialog(false);
          }}
          content={t('template.template-form-delete-message')}
          header={toTitleCase(BUTTONS.DELETE)}
          style={{ maxWidth: '500px' }}
        />
      </>
    ));

  }

  const renderTemplateFormTransitions = (globalTheme: ThemePrepared, transitions: any[], formIndex: number) => {

    const onExpandTransition = (formIndex: number, transitionIndex: number, isExpand: boolean) => {
      templateForms[formIndex].Transitions[transitionIndex].IsExpand = isExpand;
      setDatasourceOnFilters(templateForms[formIndex].Transitions[transitionIndex].TransitionGroups, getFilterProperties(templateForms[formIndex]));
      setTemplateForms([...templateForms]);
    }

    const renderTransitionGroup = (globalTheme: ThemePrepared, transitionGroups: any, transitionIndex: number, formIndex: number) => {
      const textOnlyOperators = [AutomationOperators.Contains.toLowerCase(), AutomationOperators.NotContains.toLowerCase()];
      return transitionGroups.map((group, groupIndex) => (
        <>
          <Flex column gap="gap.medium">
            <Text content={groupIndex > 0 ? LIFECYCLE.AND : LIFECYCLE.RULES} className="mr-2" weight="bold" />
            <Flex column gap="gap.medium">
              <Flex space="between">
                <SwitchButton
                  type={group.LogicalOperator}
                  changeGroupType={newGroupType => {
                    group.LogicalOperator = newGroupType;
                    templateForms[formIndex].Transitions[transitionIndex].TransitionGroups[groupIndex].LogicalOperator = newGroupType;
                    setTemplateForms([...templateForms]);
                  }}
                  globalTheme={globalTheme} />
                <Flex vAlign="center" gap="gap.medium">
                  <Button
                    icon={<AddIcon styles={{ color: globalTheme.siteVariables.colorScheme.brand.foreground }} />}
                    text
                    content={<span style={{ color: globalTheme.siteVariables.colorScheme.brand.foreground }}>{LIFECYCLE.ADD_FILTER}</span>}
                    onClick={async e => {
                      e.stopPropagation();
                      onAddNewFilterClick(formIndex, transitionIndex, groupIndex);
                    }} />
                  {groupIndex > 0 && <Button icon={<DeleteIcon height={14} width={14} stroke={`${globalTheme.siteVariables.colorScheme.brand.foreground}`}  />}
                    text iconOnly size="small"
                    onClick={async (e, data) => { e.stopPropagation(); onRemoveRuleGroupClick(formIndex, transitionIndex, groupIndex); }}
                    content={
                      <span style={{ color: globalTheme.siteVariables.colorScheme.brand.foreground, fontSize: '12px' }}>
                        {t('automation.automation-filter.remove')}
                      </span>
                    }
                  />}
                </Flex>
              </Flex>
              <Flex column gap="gap.medium">
                {group.Filters.map((filter, filterIndex) => (
                  <Flex key={filter.Id} column gap="gap.medium">
                    <Flex space="between" gap="gap.small" >
                      <Flex.Item grow={1} styles={{ flexBasis: "0" }}>
                        <Flex column gap="gap.smaller">
                          <LabelElement label={t('template.template-form-form-input')} required={false}></LabelElement>
                          <Dropdown
                            fluid styles={{ root: { width: "100%" } }}
                            items={getFilterProperties(templateForms[formIndex])}
                            placeholder={t('template.template-form-select-property')}
                            style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }}
                            value={getFilterProperties(templateForms[formIndex]).find(x=>x.key==filter.Property)}
                            onChange={(e, p) => {
                              const selectedItem = p.value as DropdownDataModel;
                              onFilterPropertySelect(formIndex, transitionIndex, groupIndex, filterIndex, selectedItem, globalTheme);
                            }}
                          />
                        </Flex>
                      </Flex.Item>
                      <Flex.Item grow={1} styles={{ flexBasis: "0" }}>
                        <Flex column gap="gap.smaller">
                          <LabelElement label={t('template.template-form-operators')} required={false}></LabelElement>
                          <Dropdown
                            fluid styles={{ root: { width: "100%" } }}
                            items={getFilterOperators(filter.Type)}
                            placeholder={t('template.template-form-select-operator')}
                            style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }}
                            value={getFilterOperators(filter.Type).filter(x => x.value == filter.Operator)}
                            onChange={(e, p) => {
                              const selectedOperator = p.value as DropdownDataModel;
                              onFilterOperatorSelect(formIndex, transitionIndex, groupIndex, filterIndex, selectedOperator, globalTheme);
                            }}
                          />
                        </Flex>
                      </Flex.Item>
                      <Flex.Item grow={1} styles={{ flexBasis: "0" }}>
                        <Flex column gap="gap.smaller">
                          {(() => {
                            switch (filter.Type) {
                              case 'Input.Date':
                              case 'Ticket.DueDate':
                              case 'Ticket.ResolutionDate':
                              case CustomPropertyDataType_LABELS.DATE:
                                return <LabelElement></LabelElement>;
                              default:
                                return <LabelElement label={LIFECYCLE.VALUE} required={false}></LabelElement>
                            }
                          })()}
                          {(() => {
                            switch (filter.Type) {
                              case 'Input.ChoiceSet':
                              case 'Ticket.Status':
                              case 'Ticket.TicketType':
                              case 'Ticket.Category':
                              case 'Ticket.Priority':
                              case 'Ticket.Team':
                              case 'Ticket.Group':
                              case 'Ticket.Assignee':
                              case CustomPropertyDataType_LABELS.LIST:
                                return <Dropdown
                                  fluid styles={{ root: { width: "100%" } }}
                                  disabled={isValueFieldDisabled(filter)}
                                  placeholder={t('automation.value-field.select-a-Value')}
                                  items={getFilterDropdownValue(formIndex, filter)}
                                  style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }}
                                  value={getFilterDropdownValue(formIndex, filter).filter(x => x.key == filter.Value)}
                                  onChange={(e, p) => {
                                    const selectedValueProp = p.value as DropdownDataModel;
                                    onFilterValueChange(formIndex, transitionIndex, groupIndex, filterIndex, selectedValueProp.key, globalTheme);
                                  }} />;
                              case 'Input.Date':
                              case 'Ticket.DueDate':
                              case 'Ticket.ResolutionDate':
                              case CustomPropertyDataType_LABELS.DATE:
                                return <DatePicker
                                  dateValue={filter.Value}
                                  disableValueField={isValueFieldDisabled(filter)}
                                  isTemplateField={true}
                                  onChangeHandler={value => {
                                    onFilterValueChange(formIndex, transitionIndex, groupIndex, filterIndex, value, globalTheme);
                                  }} />;
                              case 'Input.Toggle':
                                const formDefintion = parseIfJSON(templateForms[formIndex].Form.Definition);
                                const allInputs = findCardInputItems(formDefintion.body, currentState.customProperties);
                                const inputToggle = allInputs.filter(input => (input.type === "Input.Toggle" && input.id === filter.Property))[0];
                                const inputValueTrue = inputToggle?.valueOn ? inputToggle.valueOn : 'true';
                                const inputValueFalse = inputToggle?.valueOff ? inputToggle.valueOff : 'false';

                                return <Dropdown
                                  fluid styles={{ root: { width: "100%" } }}
                                  items={[ inputValueTrue, inputValueFalse]}
                                  style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }}
                                  value={filter.Value}
                                  onChange={(e, p) => {
                                    onFilterValueChange(formIndex, transitionIndex, groupIndex, filterIndex, p.value, globalTheme);
                                  }} />;
                              case CustomPropertyDataType_LABELS.BOOL:
                                return <Dropdown
                                  fluid styles={{ root: { width: "100%" } }}
                                  items={['True', 'False']}
                                  style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }}
                                  value={filter.Value}
                                  onChange={(e, p) => {
                                    onFilterValueChange(formIndex, transitionIndex, groupIndex, filterIndex, p.value, globalTheme);
                                  }} />;
                              case 'Ticket.Collaborators':
                              case 'Ticket.Tags':
                              case CustomPropertyDataType_LABELS.LIST + "_Multi":
                                if (textOnlyOperators.includes(filter.Operator.toLowerCase())) {
                                  return <Input clearable fluid type="text"
                                    disabled={isValueFieldDisabled(filter)}
                                    value={filter.Value}
                                    onChange={(e, p) => {
                                      onFilterValueChange(formIndex, transitionIndex, groupIndex, filterIndex, p.value, globalTheme);
                                    }} />;
                                }
                                return <Dropdown
                                  fluid styles={{ root: { width: "100%" } }}
                                  multiple
                                  disabled={isValueFieldDisabled(filter)}
                                  items={getFilterDropdownValue(formIndex, filter)}
                                  style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }}
                                  value={getMultipleFilterValue(filter.Type, filter)}
                                  onChange={(e, p) => {
                                    const selectedValues = p.value as any[];
                                    const selectedValuesCommaSeparated = selectedValues.map(item => item.key).filter(value => value != null && value !== "").join(", ");
                                    onFilterValueChange(formIndex, transitionIndex, groupIndex, filterIndex, selectedValuesCommaSeparated, globalTheme);
                                  }} />;
                              case 'Ticket.Requester':
                              case 'Ticket.AffectedUsers':
                                if (textOnlyOperators.includes(filter.Operator.toLowerCase())) {
                                  return <Input clearable fluid type="text"
                                    disabled={isValueFieldDisabled(filter)}
                                    value={filter.Value}
                                    onChange={(e, p) => {
                                      onFilterValueChange(formIndex, transitionIndex, groupIndex, filterIndex, p.value, globalTheme);
                                    }} />;
                                };
                                return <FormPeoplePickerField
                                  filter={filter}
                                  isValueFieldDisabled={isValueFieldDisabled(filter)}
                                  onSelectionChanged={async (theseUsers) => {
                                    const selectedUsers = await getSelectedUsers(theseUsers, filter.Property);
                                    onFilterValueChange(formIndex, transitionIndex, groupIndex, filterIndex, selectedUsers, globalTheme);
                                  }}
                                  usersInRole={currentState?.usersInRole}
                                  globalTheme={globalTheme}
                                />;
                              case CustomPropertyDataType_LABELS.TIME:
                                return <Flex className={CustomProperty_TimeInputWrapperClass(globalTheme.siteVariables.theme === themeNames.Dark)} style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }}>
                                    <CustomTimePicker
                                      disabled={isValueFieldDisabled(filter)}
                                      value={filter.Value}
                                      onChange={p => {
                                          const timeInput = (p.target as HTMLInputElement).value;
                                      onFilterValueChange(formIndex, transitionIndex, groupIndex, filterIndex, timeInput, globalTheme);
                                      }}
                                    />
                                    </Flex>
                              case CustomPropertyDataType_LABELS.INT:
                                return <Input type='number'
                                  disabled={isValueFieldDisabled(filter)}
                                  value={filter.Value}
                                  onChange={(e, p) => {
                                    onFilterValueChange(formIndex, transitionIndex, groupIndex, filterIndex, p.value, globalTheme);
                                  }} />;
                              case 'Input.Time':
                                return <Flex className={CustomProperty_TimeInputWrapperClass(globalTheme.siteVariables.theme === themeNames.Dark)} style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }}>
                                    <CustomTimePicker
                                      disabled={isValueFieldDisabled(filter)}
                                      value={filter.Value}
                                      onChange={p => {
                                          const timeInput = (p.target as HTMLInputElement).value;
                                      onFilterValueChange(formIndex, transitionIndex, groupIndex, filterIndex, timeInput, globalTheme);
                                      }}
                                    />
                                    </Flex>;
                              case 'Input.Number':
                              case 'Input.Text':
                              default:
                                return <Input clearable fluid type="text"
                                  disabled={isValueFieldDisabled(filter)}
                                  value={filter.Value}
                                  onChange={(e, p) => {
                                    onFilterValueChange(formIndex, transitionIndex, groupIndex, filterIndex, p.value, globalTheme);
                                  }} />;
                            }

                          })()}
                        </Flex>
                      </Flex.Item>
                    </Flex>
                    <Flex vAlign="center" hAlign="end">
                      <Button icon={<DeleteIcon height={14} width={14} stroke={`${globalTheme.siteVariables.colorScheme.brand.foreground}`} />} text iconOnly size="small"
                        onClick={async (e, data) => { e.stopPropagation(); onRemoveFilterClick(formIndex, transitionIndex, groupIndex, filterIndex); }}
                        content={
                          <span style={{ color: globalTheme.siteVariables.colorScheme.brand.foreground, fontSize: '12px' }}>
                            {t('automation.automation-filter.remove')}
                          </span>
                        }
                      />
                    </Flex>
                  </Flex>
                ))}
              </Flex>
              <Flex>

              </Flex>
            </Flex>
          </Flex>
        </>
      ));
    }

    const renderTransitionItem = (globalTheme: ThemePrepared, transition: any, transitionIndex: number, formIndex: number) => {
      return <>
        <Flex column className={`ml-10 mt-5`} style={{ width: "100%" }}>
          <Flex>
            <Toggle checked={transition.UseDefaultLogic}
              style={transition.UseDefaultLogic ? AutomationToggleStyle.Enabled : AutomationToggleStyle.Disabled}
              onChange={(_ev: React.MouseEvent<HTMLElement>, checked?: boolean) => {
                templateForms[formIndex].Transitions[transitionIndex].UseDefaultLogic = checked;
                templateForms[formIndex].Transitions[transitionIndex].UseLogicBuilder = !checked;
                setTemplateForms([...templateForms]);
              }} />
            <Text content={`${LIFECYCLE.USE_DEFAULT_LOGIC} (${t('forms.move-to-next-form')})`} className={`mr-2 ${(!transition.UseDefaultLogic) && MgtCss.disabledContainer}`} color="brand" />
          </Flex>
          <Flex >
            <Toggle checked={transition.UseLogicBuilder}
              style={transition.UseLogicBuilder ? AutomationToggleStyle.Enabled : AutomationToggleStyle.Disabled}
              onChange={(_ev: React.MouseEvent<HTMLElement>, checked?: boolean) => {
                templateForms[formIndex].Transitions[transitionIndex].UseDefaultLogic = !checked;
                templateForms[formIndex].Transitions[transitionIndex].UseLogicBuilder = checked;
                setTemplateForms([...templateForms]);
              }} />
            <Text content={LIFECYCLE.USE_LOGIC_BUILDER} className={`mr-2 ${(!transition.UseLogicBuilder) && MgtCss.disabledContainer}`} color="brand" />
          </Flex>
          {transition.UseLogicBuilder && <Flex column gap="gap.medium" >
            <Flex.Item>
              <Text content={t('template.template-form-transition-description')} className="mr-2" />
            </Flex.Item>
            <Flex.Item>
              <Flex column>
                {renderTransitionGroup(globalTheme, transition.TransitionGroups, transitionIndex, formIndex)}
              </Flex>
            </Flex.Item>
            <Flex.Item>
              <Flex space="between" gap="gap.medium" className="mr-2">
                {renderAddRuleGroupButton(globalTheme, formIndex, transitionIndex)}
              </Flex>
            </Flex.Item>
            <Flex.Item>
              <Flex space="between" gap="gap.small" className="ml-1" wrap styles={{ width: "100%", maxWidth: "100%", marginTop: "1rem" }}>
                <Flex.Item grow={1} styles={{ flexBasis: "0" }} >
                  <Flex column gap="gap.smaller">
                    <Text content={t('template.template-form-transition-action')}  weight="bold" />
                    <Dropdown
                      inverted
                      fluid
                      style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }}
                      items={FORM_ACTIONS}
                      placeholder={toTitleCase(t('template.template-form-transition-select-action'))}
                      value={FORM_ACTIONS.filter(x => x.value == transition.ActionType)}
                      onChange={(e, p) => {
                        const selectedAction = p.value as DropdownDataModel;
                        templateForms[formIndex].Transitions[transitionIndex].ActionType = selectedAction.value;
                        setTemplateForms([...templateForms]);
                        setFormDropDownList(formList == undefined ? [] : formList.map((form, i) => { return { key: form.Order, value: form.Name, label: form.Name, header: form.Name } }));
                      }}
                    />
                  </Flex>
                </Flex.Item>
                {transition.ActionType == "MoveToXForm" &&
                  <Flex.Item grow={1} styles={{ flexBasis: "0" }}>
                    <Flex column gap="gap.smaller">
                      <Text content='Form' className="mr-2" weight="bold" />
                      <Dropdown
                        inverted
                        fluid
                        style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background2 }}
                        items={formDropDownList}
                        placeholder={toTitleCase(t('template.template-form-transition-select-form'))}
                        value={formDropDownList.filter(x => x.key == transition.TransitionFormIndex)}
                        onChange={(e, p) => {
                          const selectedForm = p.value as DropdownDataModel;
                          templateForms[formIndex].Transitions[transitionIndex].TransitionFormIndex = selectedForm.key;
                          setTemplateForms([...templateForms]);
                        }}
                      />
                    </Flex>
                  </Flex.Item>
                }
              </Flex>
            </Flex.Item>
            
          </Flex>}

        </Flex>
      </>
    }

    const renderRemoveFilterButton = (globalTheme: any, transitionIndex: any) => {
      return <Flex>
        <Flex gap={`gap.small`} vAlign={`center`}>
          <Flex.Item>
            <div>
              <Button icon={<DeleteIcon height={14} width={14} stroke={`${globalTheme.siteVariables.colorScheme.brand.foreground}`} />} text iconOnly size="small"
                onClick={async e => { e.stopPropagation(); setSelectedTransitionIndexToDelete(transitionIndex); setOpenDeleteTransitionDialog(true); }}
                content={
                  <span style={{ color: globalTheme.siteVariables.colorScheme.brand.foreground, fontSize: '12px' }}>
                    {t('automation.automation-filter.remove')}
                  </span>
                }
              />
            </div>
          </Flex.Item>
        </Flex>
      </Flex>;

    }
    return transitions.map((transition, index) => (
      <>
        <Flex column style={{ width: '100%' }}>
          <Flex>
            <Flex styles={{ width: '97%' }}>
              {transition.IsExpand &&
                <Flex column gap="gap.small" styles={{ width: '100%' }}>
                  <Flex gap="gap.small" vAlign="center" space="between" style={{ borderBottom: `1px solid rgb(98, 100, 167)`, marginLeft: '10px', width: '100%' }}>
                    <Flex.Item >
                      <span onClick={() => onExpandTransition(formIndex, index, !transition.IsExpand)} className={`cursor-pointer`} title={`Collapse`}><ChevronDownIcon /></span>
                    </Flex.Item>
                    <Flex.Item >
                      <Input fluid value={transition.Name} onChange={(e: any) => {
                        templateForms[formIndex].Transitions[index].Name = e.target.value;
                        setTemplateForms([...templateForms]);
                      }}></Input>
                    </Flex.Item>
                    <Flex.Item>
                      {renderRemoveFilterButton(globalTheme, index)}
                    </Flex.Item>
                  </Flex>
                  <Flex gap="gap.small" vAlign="center">
                    {renderTransitionItem(globalTheme, transition, index, formIndex)}
                  </Flex>
                </Flex>
              }
              {!transition.IsExpand &&
                <Flex gap="gap.small" vAlign="center" space="between" style={{ marginLeft: '10px', width: '100%' }}>
                  <Flex gap="gap.small" vAlign="center">
                    <span onClick={() => onExpandTransition(formIndex, index, !transition.IsExpand)} className={`cursor-pointer`} title={`Expand`}>
                      <ChevronEndIcon />
                      <Text className={'ml-3'} content={transition.Name} />
                    </span>

                  </Flex>
                  <Flex.Item>
                    {renderRemoveFilterButton(globalTheme, index)}
                  </Flex.Item>
                </Flex>
              }
            </Flex>


          </Flex>


        </Flex>
        <Dialog
          open={openDeleteTransitionDialog}
          onCancel={() => setOpenDeleteTransitionDialog(false)}
          cancelButton={toTitleCase(BUTTONS.CANCEL)}
          confirmButton={toTitleCase(BUTTONS.DELETE)}
          onConfirm={(e) => {
            templateForms[formIndex].Transitions.splice(selectedTransitionIndexToDelete, 1);
            setOpenDeleteTransitionDialog(false);
          }}
          content={t('template.template-form-delete-transition')}
          header={toTitleCase(BUTTONS.DELETE)}
          style={{ maxWidth: '500px' }}
        />
      </>
    ));
  }

  return (
    <ProviderConsumer
      render={globalTheme => (
        <>
          <Flex column styles={{ width: '100%', ...ThemeColorScheme(globalTheme.siteVariables) }} gap="gap.smaller" >
            {renderHeader()}
            {renderFormList(globalTheme)}
            {renderAddFormButton(globalTheme)}
            {isFeatureEnabled && renderSummaryCardSection(globalTheme)}
            <br></br>
          </Flex>
          <Dialog
            open={openFormSelectionDialog}
            onCancel={() => setFormSelectionDialog(false)}
            cancelButton={toTitleCase(BUTTONS.CANCEL)}
            confirmButton={{ disabled: !selectedForm, content: toTitleCase(BUTTONS.SUBMIT) }}
            onConfirm={() => { onSubmitForm() }}
            content={renderAddFormDialog(globalTheme)}
            header={t('template.template-form-add-a-form')}
          />
          <Dialog
            open={duplicateDialogOpen}
            confirmButton={toTitleCase(BUTTONS.OK)}
            onConfirm={() => { setDuplicateDialogOpen(false) }}
            content={<Flex><Text content={t('template.template-form-duplicate-message-content')} /></Flex>}
            header={t('template.template-form-duplicate-message-title')}
          />
        </>
      )}
    />
  );

};

interface FormPeoplePickerFieldProps {
  filter: any;
  isValueFieldDisabled: boolean;
  onSelectionChanged: (platformUsers: PlatformUser[]) => Promise<void>;
  usersInRole?: any[];
  globalTheme: ThemePrepared;
}

export const FormPeoplePickerField = ({
  filter,
  isValueFieldDisabled,
  onSelectionChanged,
  usersInRole,
  globalTheme
}: FormPeoplePickerFieldProps) => {
  const [peopleValues, setPeopleValues] = useState<PlatformUser[]>([]);

  const setPeoplePickerData = (filter: any) => {
    const getUsers = async () => {
      const users = await getCachedUsers(filter.Value.split(',').filter(x => x).map(x => +x));
      setPeopleValues(users);
    };
    if(filter.Value.length > 0)
      getUsers();
    else 
      setPeopleValues([]);
  };

  useEffect(() => {
    setPeoplePickerData(filter);
  }, [filter.Value]);

  return <TikitPeoplePicker options={usersInRole} 
    isDisabled={isValueFieldDisabled}
    value={peopleValues} 
    isMultiple={filter.Type == "Ticket.AffectedUsers"}
    onChange={async (selectedUsers) => await onSelectionChanged(selectedUsers)}
    globalTheme={globalTheme}
    />
}